import { useState } from "react";
import { X, ArrowLeft } from "lucide-react";
import { StepProcess } from "./StepProcess";
import { StepProduct } from "./StepProduct";
import { StepQuantities } from "./StepQuantities";
import { StepDone } from "./StepDone";
import { candidateModels, todayStr } from "./dprUtils";

const STEPS = ["process", "product", "qty"];

export function ProductionWizard({ processes, models, links, todayCount, isOp, canBackdate, initial, onClose, onSaved, currentUser }) {
  const [step, setStep] = useState(initial ? "qty" : "process");
  const [process, setProcess] = useState(initial ? processes.find((p) => p.id === initial.process_id) || null : null);
  const [model, setModel] = useState(initial?.model || "");
  const [meta, setMeta] = useState({ date: initial?.date || todayStr(), shift: initial?.shift || "Day" });
  const [result, setResult] = useState(null);
  const [qtyKey, setQtyKey] = useState(0);

  const pickProcess = (p) => {
    setProcess(p);
    const cands = candidateModels(p.id, models, links);
    if (cands.length === 1) { setModel(cands[0].name); setStep("qty"); }
    else setStep("product");
  };
  const pickModel = (name) => { setModel(name); setStep("qty"); };
  const back = () => {
    if (step === "qty") { const c = candidateModels(process.id, models, links); setStep(c.length === 1 ? "process" : "product"); }
    else if (step === "product") setStep("process");
  };
  const saved = (r) => { setResult(r); setStep("done"); onSaved(); };
  const again = () => { setResult(null); setQtyKey((k) => k + 1); setStep("qty"); };
  const other = () => { setResult(null); setProcess(null); setModel(""); setStep("process"); };
  const stepIdx = STEPS.indexOf(step);

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-3 sm:p-6" data-testid="production-wizard">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          {!initial && step !== "process" && step !== "done" && (
            <button data-testid="wiz-back" onClick={back} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"><ArrowLeft size={18} /></button>
          )}
          <div className="flex-1">
            <h2 className="font-heading text-lg font-semibold text-slate-900">{initial ? "Edit production entry" : "Record production"}</h2>
            {!initial && step !== "done" && (
              <div className="flex items-center gap-1.5 mt-1" data-testid="wiz-progress">
                {STEPS.map((s, i) => <span key={s} className={`h-1.5 rounded-full transition-[width,background-color] ${i <= stepIdx ? "bg-blue-600 w-8" : "bg-slate-200 w-5"}`} />)}
                <span className="text-xs text-slate-400 ml-1">Step {stepIdx + 1} of 3</span>
              </div>
            )}
          </div>
          <button data-testid="wiz-close" onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {step === "process" && <StepProcess processes={processes} todayCount={todayCount} onPick={pickProcess} />}
          {step === "product" && process && <StepProduct process={process} candidates={candidateModels(process.id, models, links)} allModels={models} onPick={pickModel} />}
          {step === "qty" && process && (
            <StepQuantities key={qtyKey} process={process} model={model} processes={processes} links={links} meta={meta} setMeta={setMeta}
              isOp={isOp} canBackdate={canBackdate} initial={initial} onSaved={saved} onChangeSelection={other} currentUser={currentUser} />
          )}
          {step === "done" && result && <StepDone result={result} process={process} model={model} onAgain={again} onOther={other} onClose={onClose} />}
        </div>
      </div>
    </div>
  );
}
